import {
  ActionIcon,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconEdit,
  IconEyeSearch,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { deleteAdminPayment, getPayments } from "api/payment.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
} from "common/constants";
import { navigateTo } from "common/navigation";
import { confirmDelete, formatDate, formatNZD } from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import Reprint from "pages/admin/components/Reprint";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInvoiceStore } from "stores/useInvoiceStore";
import { PaginatedPayments } from "types/model.types";
import PaymentEntryForm from "./AdminPaymentEntryPage";
import AdminPaymentPreview from "./AdminPaymentPreview";

const AdminPaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const id = params.get("id");

  const [recordId, setRecordId] = useState<number | null>(null);
  const [preview, setPreview] = useState(false);
  const { invoices: knownInvoices, loading: invoiceLoading } =
    useInvoiceStore();
  const [payments, setPayments] = useState<PaginatedPayments>({
    data: [],
    total: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState(1);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getPayments(activePage, DEFAULT_PAGE_SIZE),
        );
        if (mounted) setPayments(response ?? null);
      } catch (error) {
        logError(error);

        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, [activePage, reloadTrigger]);

  const handlePrint = (journalId: string | number) => {
    //setRecordId(Number(journalId));
    //setPreview(true);
    navigateTo(`/print/payment?id=${journalId}`);
  };

  const handleEdit = async (journalId: string | number) => {
    setRecordId(Number(journalId));
    setEntryModalOpen(true);
  };

  const handleDelete = async (id: string | number) => {
    if (!id) return;
    if (confirmDelete() === false) return;
    try {
      await safeApiCall(deleteAdminPayment(Number(id)));
      setReloadTrigger(new Date());
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Success,
      });
    } catch {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };
  useEffect(() => {
    if (!id) return;

    handleEdit(id);
    setTimeout(() => {
      navigate("/admin/payments", { replace: true });
    }, 500);
  }, [id]);
  const rows = payments.data.map((item) => {
    return (
      <Table.Tr key={item.id}>
        <Table.Td className="action-cell action-cell-lg">
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Print">
              <ActionIcon variant="subtle" onClick={() => handlePrint(item.id)}>
                <IconEyeSearch size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={item.isProcessed ? "Edit (disabled)" : "Edit"}>
              <ActionIcon
                variant="subtle"
                color="blue"
                disabled={item.isProcessed}
                onClick={() => handleEdit(item.id)}
              >
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={item.isProcessed ? "Delete (disabled)" : "Delete"}>
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={item.isProcessed}
                onClick={() => handleDelete(item.id)}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
        <Table.Td>{item.id}</Table.Td>
        <Table.Td>{formatDate(item.createdOn)}</Table.Td>
        <Table.Td className="money">{formatNZD(item.expected)}</Table.Td>
        <Table.Td className="money">{formatNZD(item.entered)}</Table.Td>
        <Table.Td className="notes">{item.notes}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack>
      <Title order={2}>Payments</Title>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="space-between" wrap="nowrap" mb="md">
            <Group justify="flex-start" wrap="nowrap" mb="md">
              <Reprint
                onReprint={(id: number) => {
                  navigateTo(`/print/payment?id=${id}`);
                }}
              />
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setRecordId(null);
                  setEntryModalOpen(true);
                }}
              >
                Add Payment
              </Button>
            </Group>

            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Text size="sm" c="dimmed">
                Showing 30 items per page
              </Text>
              <Pagination
                total={Math.ceil(payments.total / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
          </Group>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ whiteSpace: "nowrap" }}>Actions</Table.Th>
                <Table.Th>Payment #</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th className="money">Expected</Table.Th>
                <Table.Th className="money">Entered</Table.Th>
                <Table.Th style={{ minWidth: 300 }}>Notes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(payments.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}

      <Modal
        fullScreen
        closeOnEscape={false}
        opened={entryModalOpen}
        onClose={() => {
          setEntryModalOpen(false);
          setReloadTrigger(new Date());
        }}
        title="Add Payment Entry"
        size="xl"
        centered
        withinPortal={true} // default, but explicit here
        zIndex={2000} // ensure it's above layout elements
      >
        {invoiceLoading ? (
          <Loader />
        ) : (
          <PaymentEntryForm
            recordId={recordId}
            onSaveComplete={(close: boolean) => {
              setReloadTrigger(new Date());
              if (close) setEntryModalOpen(false);
            }}
          />
        )}
      </Modal>
      <Modal
        opened={preview}
        closeOnEscape={false}
        onClose={() => setPreview(false)}
        fullScreen
        title="Payment Preview"
        size="xl"
        centered
        withinPortal={true} // default, but explicit here
        zIndex={2001} // ensure it's above layout elements
      >
        {recordId && <AdminPaymentPreview id={recordId} />}
      </Modal>
    </Stack>
  );
};
export default AdminPaymentPage;
